import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { BlogPostPage } from "@/pages/BlogPostPage";
import { getPostBySlug, postPath } from "@/lib/blog-posts";
import { getBlogTheme } from "@/lib/blog-themes";
import { breadcrumbJsonLd, hreflangLinks, SITE_URL } from "@/lib/seo";

export function buildBlogHead(slug: string | undefined, locale: "da" | "en") {
  const post = slug ? getPostBySlug(slug) : undefined;
  if (!post) {
    return {
      meta: [
        { title: locale === "da" ? "Indlægget findes ikke — Farveladeland" : "Post not found — Farveladeland" },
        { name: "robots", content: "noindex" },
      ],
    };
  }
  const c = locale === "da" ? post.da : post.en;
  const path = postPath(post);
  const theme = getBlogTheme(post.theme);
  const url = locale === "da" ? `${SITE_URL}${path}` : `${SITE_URL}/en${path}`;
  const metaTitle = c.metaTitle ?? `${c.title} — Farveladeland`;
  const metaDescription = c.metaDescription ?? c.excerpt;
  const cover = post.cover.startsWith("http") ? post.cover : `${SITE_URL}${post.cover}`;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: c.title,
    description: metaDescription,
    image: [cover, ...(post.gallery ?? [])].slice(0, 6),
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: { "@type": "Organization", name: "Farveladeland", url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: "Farveladeland",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: locale === "da" ? "da-DK" : "en",
  };

  const crumb = breadcrumbJsonLd([
    { name: locale === "da" ? "Forsiden" : "Home", url: locale === "da" ? SITE_URL : `${SITE_URL}/en` },
    { name: locale === "da" ? "Bloggen" : "Journal", url: locale === "da" ? `${SITE_URL}/blog` : `${SITE_URL}/en/blog` },
    ...(theme
      ? [
          {
            name: theme[locale].title,
            url: `${SITE_URL}${locale === "da" ? "" : "/en"}/blog/${theme.id}`,
          },
        ]
      : []),
    { name: c.title, url },
  ]);

  return {
    meta: [
      { title: metaTitle },
      { name: "description", content: metaDescription },
      { property: "og:title", content: c.title },
      { property: "og:description", content: metaDescription },
      { property: "og:image", content: cover },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:locale", content: locale === "da" ? "da_DK" : "en_US" },
      { property: "og:locale:alternate", content: locale === "da" ? "en_US" : "da_DK" },
      { property: "article:published_time", content: post.publishedAt },
      { property: "article:author", content: "Farveladeland" },
      { property: "article:section", content: theme?.[locale].title ?? "" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: cover },
    ],
    links: [
      { rel: "canonical", href: url },
      ...hreflangLinks({ path, locale }),
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(articleLd) },
      { type: "application/ld+json", children: JSON.stringify(crumb) },
    ],
  };
}


// /blog/<theme>/<post>. A post belongs to exactly one theme; a wrong theme in
// the URL 301s to the right one.
export const Route = createFileRoute("/blog/$slug_/$post")({
  loader: ({ params }) => {
    const post = getPostBySlug(params.post);
    if (!post) throw notFound();
    if (post.theme !== params.slug) {
      throw redirect({ href: `${postPath(post)}`, statusCode: 301 });
    }
    return { post };
  },
  head: ({ params }) => buildBlogHead(params?.post, "da"),
  component: BlogPostRoute,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
      <p className="eyebrow">404</p>
      <h1 className="mt-4 font-display text-5xl">Indlægget findes ikke</h1>
      <a
        href="/blog"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
      >
        Til bloggen →
      </a>
    </div>
  ),
});

function BlogPostRoute() {
  const { post } = Route.useLoaderData();
  return <BlogPostPage post={post} locale="da" />;
}
